<template>
  <div
    class="progress-bar"
    :style="{width: width + 'px'}"
  >
    <div
      v-for="(section, index) in localSections"
      :key="index"
      class="progress-section"
      :style="{ flex: section.percentage }"
      :class="[{ 'show-annotation': section.percentage < 10 }, `bg-${section.variant}`, {'d-none': !section.percentage}, {cursor: section.shipmentId}]"
      @click="redirect(section)"
      @mouseover="expandSection(index)"
      @mouseleave="resetSections"
    >
      <span class="section-label">
        <span class="section-value">
          {{ section.value }}
        </span>
        <span v-if="section.percentage >= 10">
          {{ section.label }}
        </span>
      </span>
      <span
        v-if="section.percentage < 10"
        class="annotation"
      >
        {{ section.value }}
      </span>
      <div class="progress-percentage">
        {{ section.originalPercentage }}%
      </div>
      <div
        v-if="section.percentage >= 10"
        class="progress-fade"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdvancedProgressBar',
  props: {
    sections: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      default: 500,
    },
    expandIndex: {
      type: Number,
      default: 30,
    },
  },
  data() {
    return {
      localSections: [], // Local copy of the sections prop
    }
  },
  created() {
    // Initialize localSections with a deep copy of the prop sections
    this.localSections = this.sections.map(section => ({
      ...section,
      originalPercentage: section.percentage, // Store the original percentage for reset
    }))
  },
  methods: {
    expandSection(index) {
      // Only expand if the section's percentage is equal to its original value
      this.localSections = this.localSections.map((section, i) => {
        if (i === index && section.percentage === section.originalPercentage) {
          return { ...section, percentage: section.percentage + this.expandIndex }
        }
        return section
      })
    },
    resetSections() {
      // Reset the local sections to the original percentages
      this.localSections = this.localSections.map(section => ({
        ...section,
        percentage: section.originalPercentage,
      }))
    },
    redirect(section) {
      if (section.shipmentId) {
        const url = section.link || `declarations/${section.label.replace(' ', '-')}`
        this.$router.push(`/shipments/${section.shipmentId}/${url}`)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/variables';

.progress-bar {
  display: flex;
  align-items: center;
  position: relative;
  background-color: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  height: 40px;
  flex-direction: row;
}

.progress-section {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: flex-grow 0.3s ease, width 0.3s ease;
  text-align: center;
  height: 100%;
  flex-grow: 1;
  overflow: hidden;

  &.cursor {
    cursor: pointer;
  }

  .progress-fade {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 15px;
    height: 100%;
  }

  &.bg-light {
    border-left: 3px solid darken($light, 10%);

    .section-label {
      color: $secondary;
    }

    .progress-percentage {
      background: $light;
      color: rgba($secondary, .3)
    }

    .progress-fade {
      background: linear-gradient(to right, transparent, $light);
    }
  }

  &.bg-success {
    border-left: 3px solid darken($green, 5%);

    .progress-percentage {
      background: $green;
    }

    .progress-fade {
      background: linear-gradient(to right, transparent, $green);
    }
  }
  &.bg-warning {
    border-left: 3px solid darken($orange, 10%);

    .progress-percentage {
      background: $orange;
    }

    .progress-fade {
      background: linear-gradient(to right, transparent, $orange);
    }
  }
  &.bg-primary {
    border-left: 3px solid darken($primary, 10%);

    .progress-percentage {
      background: $primary;
    }

    .progress-fade {
      background: linear-gradient(to right, transparent, $primary);
    }
  }
  &.bg-secondary {
    border-left: 3px solid darken($secondary, 10%);

    .progress-percentage {
      background: $secondary;
    }

    .progress-fade {
      background: linear-gradient(to right, transparent, $secondary);
    }
  }

  &:hover .progress-percentage {
    opacity: 1;
  }

  &:hover .progress-fade {
    right: 40px;
  }
}

.progress-section:hover {
  flex-grow: 5;
  width: 280px;
}

.section-label {
  color: #fff;
  display: block;
  position: absolute;
  top: 9px;
  left: 10px;
  opacity: 1;
  transition: opacity 0.1s ease;
}

.progress-section.show-annotation .section-label {
  opacity: 0;
}

.section-value {
  padding-right: 5px;
  font-weight: bold;
}

.annotation {
  position: absolute;
  top: 9px;
  font-size: 12px;
  color: #fff;
  font-weight: bold;
}

.progress-percentage {
  position: absolute;
  right: 0;
  top: 0;
  line-height: 40px;
  width: 40px;
  text-align: center;
  color: rgba(255,255,255, .4);
  font-weight: bold;
  opacity: 0;
  transition: opacity .1s;
}
</style>
